// File: 213-AlarmPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Bell, AlertTriangle, CheckCircle, History, Settings } from 'lucide-react';

// --- Reusable Toggle Switch Component (No changes needed here) ---
const ToggleSwitch = ({ enabled, onChange }) => {
    const toggleClass = enabled ? 'bg-green-600' : 'bg-gray-600';
    const dotClass = enabled ? 'translate-x-10' : 'translate-x-1';
    return (
        <button
            onClick={() => onChange(!enabled)}
            className={`relative inline-flex items-center h-8 rounded-full w-20 transition-colors duration-200 ease-in-out focus:outline-none ${toggleClass}`}
        >
            <span className="sr-only">Toggle Alarm</span>
            
            {enabled && (
                <span className="absolute left-2 text-xs font-bold text-white">
                    ON
                </span>
            )}

            {!enabled && (
                    <span className="absolute right-2 text-xs font-bold text-white">
                        OFF
                    </span>
            )}

            <span
                className={`inline-block w-6 h-6 transform bg-white rounded-full transition-transform duration-200 ease-in-out ${dotClass}`}
            />
        </button>
    );
};


// --- Component for Register (Analog) Setpoints (No changes needed here) ---
const RegisterSetpointRow = ({ group, onUpdate }) => {
 const [highValue, setHighValue] = useState(group.highAlarm ? group.highAlarm.threshold : '');
 const [lowValue, setLowValue] = useState(group.lowAlarm ? group.lowAlarm.threshold : '');
 const [isEnabled, setIsEnabled] = useState(
    (group.highAlarm && group.highAlarm.enabled !== false) || (group.lowAlarm && group.lowAlarm.enabled !== false)
 );

 const handleSave = () => {
    if (group.highAlarm) {
      onUpdate(group.highAlarm.key, { newThreshold: parseFloat(highValue) });
    }
    setTimeout(() => {
      if (group.lowAlarm) {
        onUpdate(group.lowAlarm.key, { newThreshold: parseFloat(lowValue) });
      }
    }, 100);
 };

 const handleToggle = (newEnabledState) => {
      setIsEnabled(newEnabledState);
      if(group.highAlarm) onUpdate(group.highAlarm.key, { newEnabledState });
      if(group.lowAlarm) setTimeout(() => onUpdate(group.lowAlarm.key, { newEnabledState }), 100);
 };

 return (
    <tr className={`border-b border-gray-700 ${!isEnabled ? 'opacity-50 bg-gray-800' : ''}`}>
      <td className="p-3 text-sm text-gray-300">{group.name}</td>
      <td className="p-3 text-sm text-gray-300">Register {group.address}</td>
      <td className="p-3">
        <input
          type="number"
          value={highValue}
          onChange={(e) => setHighValue(e.target.value)}
          className="bg-gray-900 border border-gray-600 rounded-md p-1 w-24 text-center text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="N/A"
          disabled={!group.highAlarm}
        />
      </td>
      <td className="p-3">
        <input
          type="number"
          value={lowValue}
          onChange={(e) => setLowValue(e.target.value)}
          className="bg-gray-900 border border-gray-600 rounded-md p-1 w-24 text-center text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="N/A"
          disabled={!group.lowAlarm}
        />
      </td>
      <td className="p-3 text-center">
        <ToggleSwitch enabled={isEnabled} onChange={handleToggle} />
      </td>
      <td className="p-3 text-center">
        <button 
          onClick={handleSave}
          className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors duration-200"
        >
          Save
        </button>
      </td>
    </tr>
 );
};

// --- Component for Coil (Digital) Setpoints (No changes needed here) ---
const CoilSetpointRow = ({ alarm, onUpdate }) => {
 const [triggerState, setTriggerState] = useState(alarm.triggerState);
 const [enabled, setEnabled] = useState(alarm.enabled !== false);

 const handleSave = () => {
    onUpdate(alarm.key, { newTriggerState: parseInt(triggerState, 10) });
 };
 
 const handleToggle = (newEnabledState) => {
      setEnabled(newEnabledState);
      onUpdate(alarm.key, { newEnabledState });
 };

 return (
    <tr className={`border-b border-gray-700 ${!enabled ? 'opacity-50 bg-gray-800' : ''}`}>
      <td className="p-3 text-sm text-gray-300">{alarm.name}</td>
      <td className="p-3 text-sm text-gray-300">Coil {alarm.address}</td>
      <td className="p-3" colSpan="2">
        <select
          value={triggerState}
          onChange={(e) => setTriggerState(e.target.value)}
          className="bg-gray-900 border border-gray-600 rounded-md p-1 w-full text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={1}>Alarm When ON</option>
          <option value={0}>Alarm When OFF</option>
        </select>
      </td>
       <td className="p-3 text-center">
        <ToggleSwitch enabled={enabled} onChange={handleToggle} />
      </td>
      <td className="p-3 text-center">
        <button 
          onClick={handleSave}
          className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2 px-4 rounded-lg transition-colors duration-200"
        >
          Save
        </button>
      </td>
    </tr>
 );
};

// --- Component for State-Based Alarm rows ---
const StateRegisterRow = ({ alarm, onUpdate }) => {
  const [enabled, setEnabled] = useState(alarm.enabled !== false);

  const handleToggle = (newEnabledState) => {
    setEnabled(newEnabledState);
    onUpdate(alarm.key, { newEnabledState });
  };

  return (
    <tr className={`border-b border-gray-700 ${!enabled ? 'opacity-50 bg-gray-800' : ''}`}>
      <td className="p-3 text-sm text-gray-300">{alarm.name}</td>
      <td className="p-3 text-sm text-gray-300">Register {alarm.address} (Triggers on value: {alarm.triggerValue})</td>
      <td className="p-3 text-center">
        <ToggleSwitch enabled={enabled} onChange={handleToggle} />
      </td>
    </tr>
  );
};


const HistoryRow = ({ item }) => (
  <tr className="border-b border-gray-700">
    <td className="p-3 text-sm text-gray-300">{item.subject}</td>
    <td className="p-3 text-sm text-gray-400">{item.body}</td>
    <td className="p-3 text-sm text-gray-400">{new Date(item.timestamp).toLocaleString()}</td>
    <td className="p-3 text-sm text-center">
      {item.acknowledged ? (
        <span className="text-green-400 flex items-center justify-center gap-1"><CheckCircle size={16} /> Ack'd</span>
      ) : (
        <span className="text-yellow-400 flex items-center justify-center gap-1"><AlertTriangle size={16} /> Pending</span>
      )}
    </td>
  </tr>
);

const CurrentAlarmRow = ({ alarm }) => (
    <tr className="border-b border-gray-700 font-bold">
      <td className="p-3 text-sm text-red-400 flex items-center gap-2">
        <AlertTriangle size={20} />
        <span>{alarm.name}</span>
      </td>
      <td className="p-3 text-sm text-gray-300">{alarm.type === 'register' ? `Register ${alarm.address}` : `Coil ${alarm.address}`}</td>
      <td className="p-3 text-sm text-center text-red-400">{alarm.currentValue}</td>
      <td className="p-3 text-sm text-center text-gray-300">{alarm.type === 'register' ? alarm.threshold : (alarm.triggerState === 1 ? 'ON' : 'OFF')}</td>
      <td className="p-3 text-sm text-center">{alarm.type === 'register' ? (alarm.condition === '>' ? 'Above' : 'Below') : 'Equals'}</td>
      <td className="p-3 text-sm text-center">
        <span className="px-3 py-1 rounded-full text-xs bg-red-500 text-white">Active</span>
      </td>
      <td className="p-3 text-sm text-gray-400">{new Date().toLocaleString()}</td>
      <td className="p-3 text-sm text-center">
          <button className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-1 px-3 rounded-lg transition-colors duration-200">
            Acknowledge
          </button>
      </td>
    </tr>
  );

export default function AlarmPage({ setCurrentPage }) {
  const [alarmConfig, setAlarmConfig] = useState([]);
  const [liveData, setLiveData] = useState({ coils: [], holdingRegisters: [] });
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('current');
  const [error, setError] = useState(null);





  


  // --- CHANGE 1: Use the same gateway constants as App.js ---
  const API_GATEWAY_URL = 'https://api.thomasevolutioncontrols.com';
  const DASHBOARD_ID = '214'; 









  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('jwt_token');
    if (token) { return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }; }
    return null;
  }, []);

  // --- CHANGE 2: Update all fetch calls to go through the API Gateway ---
  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) {
        setError("Authentication token not found.");
        return;
    }
    try {
        const [configRes, historyRes, dataRes] = await Promise.all([
            fetch(`${API_GATEWAY_URL}/api/alarms/config/${DASHBOARD_ID}`, { headers }),
            fetch(`${API_GATEWAY_URL}/api/alarms/history/${DASHBOARD_ID}`, { headers }),
            fetch(`${API_GATEWAY_URL}/api/scada-data/${DASHBOARD_ID}`, { headers }) // Use the correct gateway endpoint
        ]);

        if (!configRes.ok) throw new Error('Failed to fetch alarm config');
        if (!historyRes.ok) throw new Error('Failed to fetch alarm history');
        if (!dataRes.ok) throw new Error('Failed to fetch live data');

        const config = await configRes.json();
        const historyData = await historyRes.json();
        const plcData = await dataRes.json();

        setAlarmConfig(config);
        setHistory(historyData);
        setLiveData(plcData);
        setError(null); // Clear previous errors on successful fetch

    } catch (err) {
        setError(err.message);
        console.error(err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // --- CHANGE 3: Update the setpoint save endpoint ---
  const updateSetpoint = useCallback(async (key, updatePayload) => {
    const headers = getAuthHeaders();
    if (!headers) {
        setError("Cannot update setpoint: Not authenticated.");
        return;
    }
    try {
        const response = await fetch(`${API_GATEWAY_URL}/api/alarms/config/${DASHBOARD_ID}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ key, ...updatePayload })
        });
        if (!response.ok) throw new Error('Failed to save setpoint');
        fetchData(); // Refetch data to confirm change
    } catch(err) {
        setError(err.message);
    }
  }, [getAuthHeaders, fetchData]);
  
  const activeAlarms = useMemo(() => {
    if (!alarmConfig || !Array.isArray(alarmConfig) || !liveData.coils || !liveData.holdingRegisters) {
        return [];
    }
    return alarmConfig.filter(a => a.enabled !== false).map(config => {
        let currentValue;
        let isActive = false;
        const type = config.type || 'register'; 
        if (type === 'register') {
            currentValue = liveData.holdingRegisters[config.address] || 0;
            if (config.condition === '>' && currentValue > config.threshold) isActive = true;
            if (config.condition === '<' && currentValue < config.threshold) isActive = true;
        } else if (type === 'coil') {
            currentValue = liveData.coils[config.address] || 0;
            if (currentValue === config.triggerState) isActive = true;
        } else if (type === 'stateRegister') {
            currentValue = liveData.holdingRegisters[config.address] || 0;
            if (currentValue === config.triggerValue) isActive = true;
        }
        return { ...config, type, currentValue, status: isActive ? 'Active' : 'Normal' };
    }).filter(alarm => alarm.status === 'Active');
  }, [alarmConfig, liveData]);

  const { groupedRegisterAlarms, coilAlarms, stateRegisterAlarms } = useMemo(() => {
    const groups = {};
    const coils = [];
    const stateRegisters = []; // New array for state-based alarms
    if (!Array.isArray(alarmConfig)) {
        return { groupedRegisterAlarms: [], coilAlarms: [], stateRegisterAlarms: [] };
    }

    for (const alarm of alarmConfig) {
        if (alarm.type === 'coil') {
            coils.push(alarm);
        } else if (alarm.type === 'register') {
            const baseName = alarm.name.replace(/ High$| Low$/, '').trim();
            if (!groups[alarm.address]) {
                groups[alarm.address] = {
                    address: alarm.address,
                    name: baseName,
                    highAlarm: null,
                    lowAlarm: null,
                };
            }
            if (alarm.condition === '>') {
                groups[alarm.address].highAlarm = alarm;
            } else if (alarm.condition === '<') {
                groups[alarm.address].lowAlarm = alarm;
            }
        } else if (alarm.type === 'stateRegister') {
            stateRegisters.push(alarm); // Add state-based alarms to their own array
        }
    }
    return { 
        groupedRegisterAlarms: Object.values(groups), 
        coilAlarms: coils, 
        stateRegisterAlarms: stateRegisters 
    };
  }, [alarmConfig]);


  const renderContent = () => {
    if (error) {
        return <div className="p-4 text-red-500 bg-red-100 border border-red-500 rounded-md">{error}</div>
    }
    
    switch (activeTab) {
      case 'current':
        return (
          <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="p-4 bg-gray-900 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><Bell /> Current Alarms</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700 text-xs text-gray-300 uppercase tracking-wider">
                  <tr>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-center">Current Value</th>
                    <th className="p-3 text-center">Setpoint</th>
                    <th className="p-3 text-center">Condition</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-left">Timestamp</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {activeAlarms.length > 0 ? (
                    activeAlarms.map(alarm => <CurrentAlarmRow key={alarm.key} alarm={alarm} />)
                  ) : (
                    <tr><td colSpan="8" className="text-center p-8 text-gray-400">No active alarms.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'history':
        return (
          <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
             <div className="p-4 bg-gray-900 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><History /> Alarm History</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700 text-xs text-gray-300 uppercase tracking-wider">
                  <tr>
                    <th className="p-3 text-left">Alarm</th>
                    <th className="p-3 text-left">Details</th>
                    <th className="p-3 text-left">Timestamp</th>
                    <th className="p-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {history.length > 0 ? (
                    history.map(item => <HistoryRow key={item.id} item={item} />)
                  ) : (
                    <tr><td colSpan="4" className="text-center p-8 text-gray-400">No alarm history found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'setpoints':
        return (
          <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="p-4 bg-gray-900 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><Settings /> Alarm Setpoints</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700 text-xs text-gray-300 uppercase tracking-wider">
                  <tr>
                    <th className="p-3 text-left">Alarm Name</th>
                    <th className="p-3 text-left">Point</th>
                    <th className="p-3 text-left">High Setpoint</th>
                    <th className="p-3 text-left">Low Setpoint</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-center">Save</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                    {groupedRegisterAlarms.map(group => (
                        <RegisterSetpointRow key={group.address} group={group} onUpdate={updateSetpoint} />
                    ))}
                </tbody>
                <thead className="bg-gray-700 text-xs text-gray-300 uppercase tracking-wider">
                    <tr>
                        <th className="p-3 text-left">Alarm Name</th>
                        <th className="p-3 text-left">Point</th>
                        <th className="p-3 text-left" colSpan="2">Condition</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 text-center">Save</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                    {coilAlarms.map(alarm => (
                        <CoilSetpointRow key={alarm.key} alarm={alarm} onUpdate={updateSetpoint} />
                    ))}
                </tbody>
              </table>

              <div className="mt-8">
                <div className="p-4 bg-gray-900 border-b border-gray-700">
                    <h3 className="text-lg font-bold text-white">State-Based Alarms</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700 text-xs text-gray-300 uppercase tracking-wider">
                      <tr>
                        <th className="p-3 text-left">Alarm Name</th>
                        <th className="p-3 text-left">Point & Trigger</th>
                        <th className="p-3 text-center">Enable/Disable</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {stateRegisterAlarms.map(alarm => (
                        <StateRegisterRow key={alarm.key} alarm={alarm} onUpdate={updateSetpoint} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const TabButton = ({ tabName, label, icon }) => (
    <button
      onClick={() => setActiveTab(tabName)}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-200 ${
        activeTab === tabName
          ? 'bg-gray-800 text-white border-b-2 border-blue-500'
          : 'text-gray-400 hover:bg-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="bg-gray-900 min-h-screen text-white p-4 sm:p-6 lg:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <header className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-100">VRU Alarm Dashboard</h1>
          <button 
            onClick={() => setCurrentPage('dashboard')} 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Back to Main Dashboard
          </button>
        </header>

        <div className="flex border-b border-gray-700 mb-6">
            <TabButton tabName="current" label="Current Alarms" icon={<Bell size={16}/>} />
            <TabButton tabName="history" label="Alarm History" icon={<History size={16}/>} />
            <TabButton tabName="setpoints" label="Alarm Setpoints" icon={<Settings size={16}/>} />
        </div>

        <main>
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
